import type { ILanguage } from "@/graphql";

export type EnumLanguageValue = Partial<ILanguage> & {
  id?: number | null;
  __typename?: string;
};
export type EnumValueRelation = {
  __typename?: string;
  languageId?: number | null;
  orderIndex?: number | null;
  Language?: EnumLanguageValue | null;
};
export type EnumValueSource = EnumLanguageValue | EnumValueRelation;
type LanguageKey = Exclude<keyof ILanguage, "id" | "__typename">;
type TranslateFunction = (key: string) => string;

function getEnumLanguageValue(value: EnumValueSource): EnumLanguageValue {
  if ("Language" in value || "languageId" in value || "orderIndex" in value) {
    return value.Language ?? {};
  }
  return value as EnumLanguageValue;
}

export function getLanguageValue(
  value: EnumLanguageValue | null | undefined,
  locale: string,
): string {
  const localeKey = locale.replace("-", "_") as LanguageKey;
  const fallbackEntry = Object.entries(value ?? {}).find(
    ([key, entry]) => key !== "__typename" && typeof entry === "string",
  );
  const languageValue =
    value?.[localeKey] ?? value?.de ?? value?.en ?? value?.en_GB ?? fallbackEntry?.[1];
  return typeof languageValue === "string" ? languageValue : "";
}

function getEnumLabel(value: EnumValueSource, locale: string, translate?: TranslateFunction) {
  const language = getEnumLanguageValue(value);
  const inlineLabel = getLanguageValue(language, locale);
  if (inlineLabel !== "") {
    return inlineLabel;
  }
  if ("languageId" in value && value.languageId != null && translate != null) {
    return translate(`dynamic.${value.languageId}`);
  }
  if (language.id != null && translate != null) {
    return translate(`dynamic.${language.id}`);
  }
  return "";
}

export function getEnumValueKey(value: EnumValueSource) {
  const language = getEnumLanguageValue(value);
  const id = "languageId" in value ? value.languageId : language.id;
  return id == null ? getLanguageValue(language, "en") : String(id);
}

export function getEnumOptions(
  enumValues: readonly EnumValueSource[] | null | undefined,
  locale: string,
  translate?: TranslateFunction,
) {
  return (enumValues ?? [])
    .map((value, sourceIndex) => ({
      value,
      sourceIndex,
      configuredIndex:
        "orderIndex" in value && value.orderIndex != null ? value.orderIndex : sourceIndex,
    }))
    .sort((a, b) => {
      return a.configuredIndex - b.configuredIndex || a.sourceIndex - b.sourceIndex;
    })
    .map(({ value }) => ({
      label: getEnumLabel(value, locale, translate),
      value: getEnumValueKey(value),
    }));
}

export function formatEnumValue(
  enumValues: readonly EnumValueSource[] | null | undefined,
  value: string,
  locale: string,
  translate?: TranslateFunction,
) {
  const enumValue = (enumValues ?? []).find((candidate) => {
    const language = getEnumLanguageValue(candidate);
    return (
      getEnumValueKey(candidate) === value ||
      Object.values(language).some((entry) => entry === value)
    );
  });
  return enumValue == null ? value : getEnumLabel(enumValue, locale, translate);
}
